import { QueryKey } from '@tanstack/react-query';
import apiClient from '../../../services/apiClient';
import { TransformFilters } from '../../../utils';

export const OnlineInventoryAPI = {
  fetchMovements: async ({
    queryKey,
  }: {
    queryKey: QueryKey;
  }): Promise<any> => {
    const [, filters] = queryKey as [string, any?];
    const transformedFilters = TransformFilters(filters);

    const { data } = await apiClient.get('/api/own-inventory', {
      params: {
        ...transformedFilters,
      },
    });
    return data.data;
  },

  fetchInventories: async ({
    queryKey,
  }: {
    queryKey: QueryKey;
  }): Promise<any> => {
    const [, filters] = queryKey as [string, any?];
    const transformedFilters = TransformFilters(filters);

    const { data } = await apiClient.get('/api/v2/own-inventory', {
      params: {
        ...transformedFilters,
      },
    });
    return data.data;
  },

  fetchMovementsArchived: async () => {
    const { data } = await apiClient.get('api/own-inventory/', {
      params: { 'filter[is_archived]': 1 },
    });
    return data.data;
  },

  createMovement: async (movement: any) => {
    const { data } = await apiClient.post('/own-inventory', movement);
    return data;
  },
  fetchMovementDetail: async (ticketId: string) => {
    const { data } = await apiClient.get(`/own-inventory/${ticketId}`);
    return data;
  },
  fetchAdvancedFilters: async () => {
    const { data } = await apiClient.get('api/utils/advance-filters/asset');
    return data;
  },
  fetchFisicalLocation: async () => {
    const { data } = await apiClient.get('api/utils/advance-filters/asset');
    return data;
  },

  fetchAssetDetails: async (assetId: string) => {
    try {
      const { data } = await apiClient.get(`/api/asset/${assetId}`);
      return data;
    } catch (error) {
      console.error('Error fetching asset details:', error);
      return null;
    }
  },

  fetchParameters: async (keys: string[]) => {
    try {
      const queryParams = keys
        .map((key) => `keys[]=${encodeURIComponent(key)}`)
        .join('&');

      const { data } = await apiClient.get(
        `api/utils/select-tables?${queryParams}`
      );
      return data;
    } catch (error) {
      console.error('Error fetching parameters:', error);
      return null;
    }
  },

  updateLocation: async (recordId: number, locationId: number) => {
    try {
      const { data } = await apiClient.post(
        `/api/own-inventory/update-location/${recordId}`,
        { location_id: locationId }
      );
      return data;
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  },

  updateAssetLocations: async (payload: {
    inventory_id: number;
    new_location_id: number;
    assets: { asset_id: number }[];
  }) => {
    try {
      const { data } = await apiClient.patch(
        '/api/v2/own-inventory/update-location',
        payload
      );
      return data;
    } catch (error) {
      console.error('Error al actualizar ubicación de activos:', error);
      throw error;
    }
  },

  storeAssetEvent: async (formData: FormData) => {
    try {
      const { data } = await apiClient.post(
        '/api/own-inventory/store-asset-event',
        formData
      );
      return data;
    } catch (error) {
      console.error('Error al almacenar evento:', error);
      throw error;
    }
  },

  storeAssetEventV2: async (formData: FormData) => {
    try {
      const { data } = await apiClient.post(
        '/api/v2/own-inventory/store-asset-event',
        formData
      );
      return data;
    } catch (error) {
      console.error('Error al almacenar evento:', error);
      throw error;
    }
  },

  toggleAssetCheck: async (assetId: number, formData: FormData) => {
    try {
      const { data } = await apiClient.post(
        `/api/v2/own-inventory/toggle-check/${assetId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    } catch (error) {
      console.error('Error al actualizar la verificación física:', error);
      throw error;
    }
  },

  toggleCheckPost: async (assetId: number, formData: FormData) => {
    try {
      const { data } = await apiClient.post(
        `/api/v2/own-inventory/toggle-check/${assetId}`,
        formData
      );
      return data;
    } catch (error) {
      console.error('Error al actualizar la verificación física:', error);
      throw error;
    }
  },

  updateAssetStatuses: async (payload: {
    inventory_id: number;
    new_status_id: number;
    assets: { asset_id: number }[];
  }) => {
    try {
      const { data } = await apiClient.patch(
        '/api/v2/own-inventory/update-status',
        payload
      );
      return data;
    } catch (error) {
      console.error('Error al actualizar el estado de los activos:', error);
      throw error;
    }
  },

  toggleArchiveAssets: async (assetIds: number[]) => {
    try {
      const { data } = await apiClient.patch(
        '/api/own-inventory/toggle-archived',
        {
          asset_ids: assetIds,
        }
      );
      return data;
    } catch (error) {
      console.error('Error al archivar solicitudes:', error);
      throw error;
    }
  },

  fetchAssetPetitions: async (assetId: number) => {
    try {
      const { data } = await apiClient.get(
        `/api/own-inventory/getAssetPetitions/${assetId}`
      );
      return data.data || [];
    } catch (error) {
      console.error('Error al obtener el historial de solicitudes:', error);
      throw error;
    }
  },

  fetchFilteredMovements: async (filters: Record<string, string>) => {
    const transformedFilters = TransformFilters(filters);
    try {
      const { data } = await apiClient.get(`/api/own-inventory`, {
        params: {
          ...transformedFilters,
        },
      });
      return data.data;
    } catch (error) {
      console.error('Error al obtener los datos filtrados', error);
      throw error;
    }
  },

  showVerification: async (inventoryId: number, assetId: number) => {
    try {
      const { data } = await apiClient.get(
        `/api/v2/own-inventory/${inventoryId}/${assetId}`
      );
      return data;
    } catch (error) {
      console.error('Error al obtener la verificación del activo:', error);
      throw error;
    }
  },

  storeInventory: async (formData: FormData) => {
    try {
      const { data } = await apiClient.post('/api/v2/own-inventory', formData);
      return data;
    } catch (error) {
      console.error('Error al crear el inventario:', error);
      throw error;
    }
  },

  getUserInventories: async () => {
    const { data } = await apiClient.get(
      'api/v2/own-inventory/user-inventories'
    );
    return data;
  },

  changeInventoryStatus: async (inventoryId: number, statusId: number) => {
    const { data } = await apiClient.patch(
      `api/v2/own-inventory/toogle-inventory-status/${inventoryId}`,
      { status_id: statusId }
    );
    return data;
  },
};
